import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Sync as SyncIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { syncService } from '../services/syncService';

function SyncStatus({ isAdmin = false }) {
  const [syncStatus, setSyncStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showConflicts, setShowConflicts] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    loadSyncStatus();
  }, []);

  const loadSyncStatus = () => {
    const status = syncService.getSyncStatus();
    setSyncStatus(status);
  };

  const handleSync = async () => {
    setIsLoading(true);
    setSyncResult(null);
    
    try {
      const result = await syncService.performSafeSync();
      setSyncResult(result);
      
      if (result.conflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setShowConflicts(true);
      }
      
      // Reload status after sync
      loadSyncStatus();
      
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncResult({
        success: false,
        message: `Sync failed: ${error.message}`,
        synced: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForceSync = async () => {
    if (!isAdmin) {
      alert('Force sync requires admin privileges');
      return;
    }
    
    setIsLoading(true);
    setSyncResult(null);
    
    try {
      const result = await syncService.forceSync();
      setSyncResult(result);
      loadSyncStatus();
      
    } catch (error) {
      console.error('Force sync failed:', error);
      setSyncResult({
        success: false,
        message: `Force sync failed: ${error.message}`,
        synced: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = () => {
    if (!syncStatus) return 'default';
    if (syncStatus.needsSync) return 'warning';
    return 'success';
  };

  const getStatusIcon = () => {
    if (!syncStatus) return <SyncIcon />;
    if (syncStatus.needsSync) return <WarningIcon />;
    return <CheckCircleIcon />;
  };

  const formatTimeAgo = (minutes) => {
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SyncIcon />
              Sync Status
            </Typography>
            <Chip
              icon={getStatusIcon()}
              label={syncStatus?.needsSync ? 'Needs Sync' : 'Up to Date'}
              color={getStatusColor()}
              variant="outlined"
            />
          </Box>

          {syncStatus && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last sync: {syncStatus.lastSyncTime 
                  ? formatTimeAgo(syncStatus.lastSyncAgo)
                  : 'Never'
                }
              </Typography>
              {syncStatus.lastSyncTime && (
                <Typography variant="caption" color="text.secondary">
                  {syncStatus.lastSyncTime.toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={isLoading}
              size="small"
            >
              {isLoading ? 'Syncing...' : 'Sync Now'}
            </Button>
            
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleForceSync}
                disabled={isLoading}
                size="small"
                color="warning"
              >
                Force Sync
              </Button>
            )}
          </Box>

          {syncResult && (
            <Alert 
              severity={syncResult.success ? 'success' : 'error'}
              sx={{ mb: 2 }}
            >
              {syncResult.message}
              {syncResult.changesApplied && (
                <Typography variant="caption" display="block">
                  Applied {syncResult.changesApplied} changes
                </Typography>
              )}
            </Alert>
          )}

          {syncStatus?.needsSync && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Your local data may be out of sync with the server. 
              Click "Sync Now" to check for updates.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Conflicts Dialog */}
      <Dialog open={showConflicts} onClose={() => setShowConflicts(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Sync Conflicts Detected
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following conflicts were detected during sync. 
            Manual review is required to resolve these conflicts.
          </Typography>
          
          <List>
            {conflicts.map((conflict, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>
                    <ErrorIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary={conflict.message}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          Record: {conflict.recordId}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Local: {conflict.localTimestamp?.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Remote: {conflict.remoteTimestamp?.toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < conflicts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConflicts(false)}>
            Close
          </Button>
          {isAdmin && (
            <Button 
              variant="contained" 
              color="warning"
              onClick={() => {
                handleForceSync();
                setShowConflicts(false);
              }}
            >
              Force Override
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SyncStatus;
